import { breakpoints, Card, Column, Columns, Heading, Stack } from '@etg/wings';
import { ConnectedInput } from '@eti/form';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { FormSection } from 'redux-form';
import { AGENCY_FORM_SECTION, agencyFields, fieldRestrictions } from '../constants/formFields';
import type { Country } from '../models';
import type { PhoneType } from './contact-form/Phone';
import Phone from './contact-form/Phone';

const fieldsetStyles = css`
  border: none;
`;

const columnWidth = { [breakpoints._0]: '4/4', [breakpoints._768]: '2/4' } as const;

interface AgentInformationFormSectionProps {
  countries: Country[];
  form: string;
  hasAsyncAgentPhoneError?: boolean;
  hasOtherAsyncErrors?: boolean;
  onCountryChange: (phoneType: PhoneType) => void;
}

const AgentInformationFormSection = ({
  countries = [],
  form,
  hasAsyncAgentPhoneError = false,
  hasOtherAsyncErrors = false,
  onCountryChange,
}: AgentInformationFormSectionProps) => {
  const { t } = useTranslation();
  const shouldClearPhoneInput = hasAsyncAgentPhoneError && hasOtherAsyncErrors;

  return (
    <Card data-agentinformationscrollanchor>
      <FormSection name={AGENCY_FORM_SECTION}>
        <fieldset aria-labelledby="agencyform-title" className={fieldsetStyles}>
          <Stack>
            <Heading id="agencyform-title" level={2}>
              {t('TravelerDetails.AgencyForm.Title')}
            </Heading>
            <Columns spacing={24}>
              <Column width={columnWidth}>
                <ConnectedInput
                  isReadOnly
                  label={t('TravelerDetails.AgencyForm.AgencyName.InputLabel')}
                  name={agencyFields.NAME}
                />
              </Column>
              <Column width={columnWidth}>
                <ConnectedInput
                  isReadOnly
                  label={t('TravelerDetails.AgencyForm.AgencyId.InputLabel')}
                  name={agencyFields.OFFICE_ID}
                />
              </Column>
              <Column width={columnWidth}>
                <ConnectedInput
                  autoCorrect="off"
                  label={t('TravelerDetails.AgencyForm.AgentFirstName.InputLabel')}
                  name={agencyFields.AGENT_FIRST_NAME}
                  shouldMarkInputAsRequired
                  spellCheck="false"
                  validationRuleName={agencyFields.AGENT_FIRST_NAME}
                />
              </Column>
              <Column width={columnWidth}>
                <ConnectedInput
                  autoCorrect="off"
                  label={t('TravelerDetails.AgencyForm.AgentLastName.InputLabel')}
                  name={agencyFields.AGENT_LAST_NAME}
                  shouldMarkInputAsRequired
                  spellCheck="false"
                  validationRuleName={agencyFields.AGENT_LAST_NAME}
                />
              </Column>
              <Column width={columnWidth}>
                <ConnectedInput
                  autoCapitalize="off"
                  autoCorrect="off"
                  description={t('TravelerDetails.AgencyForm.AgentEmail.Description')}
                  label={t('TravelerDetails.AgencyForm.AgentEmail.InputLabel')}
                  maxLength={fieldRestrictions.MAX_CHARS}
                  name={agencyFields.AGENT_EMAIL}
                  shouldMarkInputAsRequired
                  spellCheck="false"
                  type="email"
                  validationRuleName={agencyFields.AGENT_EMAIL}
                />
              </Column>
              <Column width={columnWidth}>
                <Phone
                  countries={countries}
                  countryCodeLabel={t(
                    'TravelerDetails.AgencyForm.AgentPhoneCountryCode.InputLabel',
                  )}
                  data-testid="agent-phone-input"
                  description={t('TravelerDetails.AgencyForm.AgentPhone.Description')}
                  form={form}
                  formSection={AGENCY_FORM_SECTION}
                  hasAsyncError={hasAsyncAgentPhoneError}
                  id={agencyFields.AGENT_PHONE}
                  label={t('TravelerDetails.AgencyForm.AgentPhone.InputLabel')}
                  name={agencyFields.AGENT_PHONE}
                  onCountryChange={shouldClearPhoneInput ? onCountryChange : undefined}
                  phoneCountryId={agencyFields.AGENT_PHONE_COUNTRY_ID}
                  shouldMarkInputAsRequired
                />
              </Column>
            </Columns>
          </Stack>
        </fieldset>
      </FormSection>
    </Card>
  );
};

export default AgentInformationFormSection;
